import * as React from 'react';
import classnames from 'classnames';
import isFunction from 'lodash/isFunction';
import isNil from 'lodash/isNil';
import isNumber from 'lodash/isNumber';

import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';

import { __t, applyTranslations } from 'utils/language';
import { getElementInfo } from 'utils/element';

import './content-slide-down.scss';

applyTranslations(require('./content-slide-down.i18n.json'));

import type {
  ContentSlideDownToggleState
} from './types';

type Props = {
  trigger: React.ReactNode,
  children: React.ReactNode,
  defaultToggleState?: ContentSlideDownToggleState,
  width?: number,
  withTriggerInSlidedown?: boolean,
  withChevron?: boolean,
  onToggle?: (updatedToggleState: ContentSlideDownToggleState) => void
};

const MSG_KEY = 'content_slide_down';

const ContentSlideDown: React.FC<Props> = ({
  trigger,
  children,
  defaultToggleState = 'collapsed',
  width,
  withTriggerInSlidedown,
  withChevron,
  onToggle
}): JSX.Element => {
  const [initialContentHeight, setInitialContentHeight] = React.useState<number | undefined>(undefined);
  const [toggleState, setToggleState] = React.useState(defaultToggleState);

  const contentRef: React.RefObject<HTMLDivElement> = React.createRef();
  let styles = {};

  if (isNumber(width)) {
    styles = {
      width,
      gridTemplateColumns: 'unset'
    };
  }

  const handleToggle = (): void => {
    const state = toggleState === 'expanded'
      ? 'collapsed'
      : 'expanded';

    setToggleState(state);

    if (isFunction(onToggle)) {
      onToggle(state);
    }
  };

  const renderChevron = (): React.ReactNode => {
    const chevronClasses = classnames('content-slide-down__chevron', {
      [`content-slide-down__chevron--${toggleState}`]: Boolean(toggleState)
    });

    return (
      <div className={chevronClasses}>
        <SVGDynamicIcon
          name="chevron-down-custom-fg"
          width={10}
          height={5}
          customColor="grey"
          clickable
        />
      </div>
    );
  };

  const renderClose = (): React.ReactNode => (
    <div
      className="content-slide-down__close"
      onClick={handleToggle}
    >
      {__t(`${MSG_KEY}.show_less`)}
      {withChevron && renderChevron()}
    </div>
  );

  const renderTrigger = (
    inSlidedown: boolean = false
  ): React.ReactNode => {
    const triggerClasses = classnames('content-slide-down__trigger', {
      'content-slide-down__children-trigger': inSlidedown
    });

    return (
      <div
        className={triggerClasses}
        onClick={handleToggle}
      >
        {trigger}
        {withChevron && !inSlidedown && renderChevron()}
      </div>
    );
  };

  const renderChildren = (): React.ReactNode => {
    const childrenClasses = classnames('content-slide-down__children', {
      'content-slide-down__children--loading': isNil(initialContentHeight),
      'content-slide-down__children--loaded': isNumber(initialContentHeight),
      'content-slide-down__children--expanded': toggleState === 'expanded',
      'content-slide-down__children--collapsed': toggleState === 'collapsed' && !isNil(initialContentHeight)
    });

    const childrenComponentClasses = classnames('content-slide-down__children-component', {
      'content-slide-down__children-component--with-trigger': withTriggerInSlidedown
    });

    return (
      <div
        ref={contentRef}
        className={childrenClasses}
        style={{ height: toggleState === 'expanded' ? initialContentHeight : undefined }}
      >
        <div className={childrenComponentClasses}>
          {children}
        </div>
        {withTriggerInSlidedown && renderTrigger(true)}
      </div>
    );
  };

  React.useEffect(() => {
    if (isNil(initialContentHeight)) {
      const contentInfo = getElementInfo(contentRef.current);
      const height = (contentInfo?.height ?? 0) + 16; // top/bottom padding

      setInitialContentHeight(height);
    }
  });

  return (
    <div
      className="content-slide-down"
      style={{ ...styles }}
    >
      {renderChildren()}
      {toggleState === 'collapsed' && renderTrigger()}
      {toggleState === 'expanded' && renderClose()}
    </div>
  );
};

export default ContentSlideDown;
